import { useEffect, useRef, useState } from "react";
import { money, calcTotals, shortDate, isEquipmentPageJob } from "@/lib/invoice";
import LayoutInvoice from "@/components/invoices/LayoutInvoice";

const PAPER = 816;
const RULE = "repeating-linear-gradient(to bottom, transparent 0, transparent calc(var(--lh) - 1px), #555 calc(var(--lh) - 1px), #555 var(--lh))";

function U({ children, className = "", w, grow }) {
  return <span className={`inline-block border-b border-black px-1 align-baseline whitespace-nowrap overflow-hidden ${grow ? "flex-1" : ""} ${className}`} style={{ minWidth: w }}>{children || "\u00a0"}</span>;
}

function Row({ children }) {
  return <p className="flex items-end gap-1 whitespace-nowrap">{children}</p>;
}

function Ruled({ text, lines, lh = 23, size = 15, testId }) {
  return (
    <div data-testid={testId} className="whitespace-pre-wrap" style={{ "--lh": `${lh}px`, lineHeight: `${lh}px`, fontSize: size, backgroundImage: RULE, minHeight: lines * lh }}>
      {text}
    </div>
  );
}

const numbered = (arr) => (arr || []).filter((x) => String(x).trim()).map((x, i) => `${i + 1}) ${x}`).join("\n");

export function FitWidth({ children }) {
  const ref = useRef(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setZoom(Math.min(1, e.contentRect.width / PAPER)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} className="w-full min-w-0 overflow-hidden"><div className="fit-paper" style={{ zoom }}>{children}</div></div>;
}

export function InvoicePage2({ invoice: v, settings: s }) {
  const p = v.page2 || {};
  const groups = [["County code", "County Tax"], ["Cty code", "Cty tax"], ["SpecTax 1", "Sp 1 code", "Sp 1 amt"], ["SpecTax 2", "Sp 2 code", "Sp 2 amt"], ["SpecTax 3", "Sp 3 code", "Sp 3 amt"]];
  return (
    <div className="invoice-paper bg-white text-black shadow-xl mx-auto" style={{ width: PAPER, minHeight: 1056, padding: "28px 56px 20px 68px", fontFamily: "'Times New Roman', Tinos, Times, serif" }} data-testid="invoice-page2">
      <div className="flex items-end justify-between border-b border-black pb-1">
        <p className="italic font-bold text-[24px]">{s.company_name}</p>
        <p className="font-bold text-[20px]">Invoice #: <U w={150}>{v.number}</U> &nbsp;Page 2</p>
      </div>
      <p className="text-[15px] mt-2"><span className="font-bold">{v.customer?.name}</span> · Service Date: {shortDate(v.service_date)}</p>
      <p className="font-bold text-[20px] mt-5">Special Notes</p>
      <Ruled text={p["Special Notes"]} lines={10} lh={24} size={15} testId="invoice-page2-notes" />
      <p className="font-bold text-[20px] mt-6">Tax Details</p>
      <div className="text-[15px] leading-[30px] mt-1" data-testid="invoice-page2-taxes">
        {groups.map((g) => <Row key={g[0]}>{g.map((k) => <span key={k} className="flex-1 flex items-end gap-1">{k}: <U grow>{p[k]}</U></span>)}</Row>)}
      </div>
    </div>
  );
}

export default function InvoiceDocument({ invoice: v, settings: s, copyLabel }) {
  if (s?.layout?.items?.length) return <LayoutInvoice invoice={v} settings={s} layout={s.layout} copyLabel={copyLabel} />;
  return <ClassicInvoice invoice={v} settings={s} copyLabel={copyLabel} />;
}

function ClassicInvoice({ invoice: v, settings: s, copyLabel }) {
  const t = calcTotals(v);
  const c = v.customer;
  const attach = isEquipmentPageJob(c?.name);
  return (
    <div className="invoice-paper bg-white text-black shadow-xl mx-auto relative" style={{ width: PAPER, minHeight: 1056, padding: "28px 56px 20px 68px", fontFamily: "'Times New Roman', Tinos, Times, serif" }} data-testid="invoice-document">
      {copyLabel && <p className="absolute top-1 right-5 font-bold text-[18px]" data-testid="invoice-copy-label">{copyLabel}</p>}
      <div className="grid grid-cols-[1fr_1fr] gap-4 pb-1 border-b border-black">
        <div className="text-center font-bold leading-tight">
          <p className="italic text-[30px]" data-testid="invoice-company-name">{s.company_name}</p>
          <p className="text-[20px]">{s.address_line1}</p>
          <p className="text-[20px]">{s.address_line2}</p>
          <p className="text-[15px] mt-1">{s.email}</p>
        </div>
        <div className="font-bold text-[27px] leading-[38px] pl-8">
          <p>Invoice #: <U w={170} className="font-bold" ><span data-testid="invoice-number">{v.number || "—"}</span></U></p>
          <p>Phone #: &nbsp;{s.phone}</p>
          <p>Toll Free#: {s.toll_free}</p>
        </div>
      </div>

      <div className="grid grid-cols-[1.15fr_1fr] gap-x-6 text-[15px] leading-[27px] mt-1">
        <div>
          <Row><U grow className="font-bold"><span data-testid="invoice-customer-name">{c.name}</span></U></Row>
          <Row><U w={260}>{c.address}</U></Row>
          <Row><U w={260}>{c.city_state_zip}</U></Row>
          <Row>Phone Number: <U w={190} className="font-bold text-center">{c.phone}</U></Row>
        </div>
        <div>
          <Row>Tech: <U w={36}>{v.tech}</U> Service Date: <U grow className="text-right">{shortDate(v.service_date)}</U></Row>
          <Row>Administrator: <U grow>{c.administrator}</U></Row>
          <Row>Contact: <U grow>{c.contact}</U></Row>
          <Row>Corporate Info.: <U grow className="font-bold">{c.corporate_info}</U></Row>
        </div>
      </div>
      <p className="text-[15px] leading-[24px] flex items-end whitespace-nowrap" data-testid="invoice-months">
        {v.months.map((m, i) => <span key={i} className="mr-1 inline-flex items-end">{i + 1}:<U w={22} className="font-bold text-center leading-[20px]">{m ? "x" : ""}</U></span>)}
        <span className="ml-1">Frequency:</span><U grow className="text-center">{v.frequency}</U>
      </p>

      <p className="font-bold text-[20px] leading-tight">Service Performed</p>
      <Ruled text={v.service_performed} lines={6} lh={21} size={14.5} testId="invoice-service-performed" />

      <div className="flex items-end gap-8 mt-4 text-[15px] font-bold">
        <span>Scales</span>
        <span className="flex-1 flex items-end gap-1">Sticker Nos: <span className="flex-1 border-b border-black text-center text-[19px]">{attach ? "\u00a0" : (v.sticker_nos || "\u00a0")}</span></span>
      </div>
      <Ruled text={attach ? "SEE ATTACHED" : numbered(v.scales)} lines={6} lh={23} size={17.5} testId="invoice-scales" />

      <p className="font-bold text-[15px] mt-3">Other Equipment</p>
      <Ruled text={attach ? "" : numbered(v.other_equipment)} lines={3} lh={23} size={17.5} testId="invoice-other-equipment" />

      <p className="font-bold text-[15px] mt-3">Lifts</p>
      <Ruled text={attach ? "" : numbered(v.lifts)} lines={6} lh={23} size={17.5} testId="invoice-lifts" />

      <div className="grid grid-cols-[1fr_250px] gap-4 mt-1 text-[15px]">
        <div className="pt-6 space-y-9">
          <p className="flex gap-2"><span className="border-b border-black flex-[2]">Technician's Signature:</span><span className="border-b border-black flex-1">Date:</span></p>
          <p className="flex gap-2"><span className="border-b border-black flex-[2]">Customer's Signature:</span><span className="border-b border-black flex-1">Date:</span></p>
        </div>
        <dl className="grid grid-cols-[1fr_150px] items-end gap-x-2 text-right">
          <dt>Subtotal:</dt><dd className="text-[22px] border-b border-black">{money(v.subtotal)}</dd>
          <dt>Tax:</dt><dd className="text-[22px] border-b border-black" data-testid="invoice-tax">{money(t.tax)}</dd>
          <dt className="font-bold mt-2">Total:</dt><dd className="text-[22px] font-bold border-b border-black" data-testid="invoice-total">{money(t.total)}</dd>
          <dt className="font-bold">Parts:</dt><dd className="text-[22px] border-b border-black" data-testid="invoice-parts-amount">{Number(v.parts) ? money(v.parts) : "\u00A0"}</dd>
          <dt className="font-bold text-[20px]">+Total:</dt><dd className="text-[22px] font-bold border-b border-black" data-testid="invoice-grand-total">{v.plus_total !== null && v.plus_total !== undefined && v.plus_total !== "" ? money(t.grand_total) : "\u00A0"}</dd>
          <dt className="col-span-2 text-left pl-12 text-blue-700 font-bold text-[13px]">{v.terms}</dt>
        </dl>
      </div>
    </div>
  );
}


// Dedicated equipment page (Scales, Other Equipment, Lifts) that always prints with the invoice.
// Ruled line counts auto-grow with the list length so long equipment lists are never cramped.
// "SEE ATTACHED" placeholder lines are dropped here (that note only belongs on page 1).
const noSeeAttached = (arr) => (arr || []).filter((x) => String(x).trim() && !/^see\s*attached$/i.test(String(x).trim()));
const equipLines = (arr, min) => Math.max(min, noSeeAttached(arr).length);
export function InvoiceEquipmentPage({ invoice: v, settings: s }) {
  return (
    <div className="invoice-paper bg-white text-black shadow-xl mx-auto" style={{ width: PAPER, minHeight: 1056, padding: "28px 56px 20px 68px", fontFamily: "'Times New Roman', Tinos, Times, serif" }} data-testid="invoice-equipment-page">
      <div className="flex justify-between items-start">
        <p className="font-bold text-[22px] leading-tight">{s?.company_name || "Equipment"}</p>
        <div className="text-right text-[15px]">
          <p>Invoice #: <span className="font-bold">{v.number}</span></p>
          <p>Service Date: {shortDate(v.service_date)}</p>
        </div>
      </div>
      <p className="text-[16px] mt-1 font-bold">{v.customer?.name}</p>

      <div className="flex items-end gap-8 mt-6 text-[15px] font-bold">
        <span>Scales</span>
        <span className="flex-1 flex items-end gap-1">Sticker Nos: <span className="flex-1 border-b border-black text-center text-[19px]">{v.sticker_nos || "\u00a0"}</span></span>
      </div>
      <Ruled text={numbered(noSeeAttached(v.scales))} lines={equipLines(v.scales, 16)} lh={26} size={17.5} testId="equip-scales" />

      <p className="font-bold text-[15px] mt-5">Other Equipment</p>
      <Ruled text={numbered(noSeeAttached(v.other_equipment))} lines={equipLines(v.other_equipment, 6)} lh={26} size={17.5} testId="equip-other-equipment" />

      <p className="font-bold text-[15px] mt-5">Lifts</p>
      <Ruled text={numbered(noSeeAttached(v.lifts))} lines={equipLines(v.lifts, 10)} lh={26} size={17.5} testId="equip-lifts" />
    </div>
  );
}
